import React from 'react';
import {
  Typography,
  Stack,
  Box,
  Tooltip,
  Link as MuiLink,
  Skeleton,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { getRatingData, useReviewsHistogram } from '@marageti/z4-lib';

const CardTextWithIcon = ({ content, icon }: { content: any, icon: any }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    {icon}
    <Typography variant="body" color="primary.light" component="div">
      {content}
    </Typography>
  </Stack>
);

type StandardSalesProps = {
  standardConversionRate: number;
  standardSaleCount: number;
};

const StandardSales = ({ standardConversionRate, standardSaleCount }: StandardSalesProps) => {
  const conversionRateFormatted = (standardConversionRate * 100).toFixed(2);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Stack>
        <Tooltip title="For Standard Sales (30-210 days)" placement="top">
          <Typography variant="bodyBold" component="div">{`${conversionRateFormatted}% Sales Conversion`}</Typography>
        </Tooltip>
        <Typography component="div">{`${standardSaleCount} Sold`}</Typography>
      </Stack>
    </Box>
  );
};

type RatingProps = {
  partnerId: string;
};

const Rating = ({ partnerId }: RatingProps) => {
  const { histogramAggregation, loading: loadingHistogram, error: errorHistogram } = useReviewsHistogram({ partnerId });

  if (loadingHistogram) {
    return <Skeleton variant="text" width={100} height={20} />;
  }

  if (errorHistogram) {
    return null;
  }

  const { average, total } = getRatingData(histogramAggregation || []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Stack>
        <MuiLink
          component={Link}
          to={`/partners/${partnerId}/reviews`}
          target="_blank"
          variant="bodyBold"
          sx={{
            color: 'primary.light',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {average ? `${average.toFixed(1)} Rating` : 'No Rating'}
        </MuiLink>
        <Typography component="div">{`${total} Traveler Reviews`}</Typography>
      </Stack>
    </Box>
  );
};

type LeadsProps = {
  leadsNow?: number;
  leadsLast3Days?: number;
  leadsLast7Days?: number;
  leadsLast14Days?: number;
  leadsLate?: number;
};

const Leads = ({
  leadsNow = 0,
  leadsLast3Days = 0,
  leadsLast7Days = 0,
  leadsLast14Days = 0,
  leadsLate = 0,
}: LeadsProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    <Box>
      <Typography component="span" variant="bodyBold">New:</Typography>
      {` ${leadsNow === 0 ? '0' : `${leadsNow} ${leadsNow > 1 ? 'Matches' : 'Match'}`}`}
    </Box>
    <Box>
      <Typography component="span" variant="bodyBold">3d:</Typography>
      {` ${leadsLast3Days === 0 ? '0' : `${leadsLast3Days} ${leadsLast3Days > 1 ? 'Matches' : 'Match'}`}`}
    </Box>
    <Box>
      <Typography component="span" variant="bodyBold">7d:</Typography>
      {` ${leadsLast7Days === 0 ? '0' : `${leadsLast7Days} ${leadsLast7Days > 1 ? 'Matches' : 'Match'}`}`}
    </Box>
    <Box>
      <Typography component="span" variant="bodyBold">14d:</Typography>
      {` ${leadsLast14Days === 0 ? '0' : `${leadsLast14Days} ${leadsLast14Days > 1 ? 'Matches' : 'Match'}`}`}
    </Box>
    <Box>
      <Typography component="span" variant="bodyBold" color={leadsLate > 0 ? 'error.dark' : 'light'}>Late:</Typography>
      <Typography component="span" variant="body" color={leadsLate > 0 ? 'error.dark' : 'light'}>
        {` ${leadsLate === 0 ? '0' : `${leadsLate} ${leadsLate > 1 ? 'Matches' : 'Match'}`}`}
      </Typography>
    </Box>
  </Box>
);

export {
  CardTextWithIcon, StandardSales, Rating, Leads,
};
